'use client'

import { Fragment, useEffect, useState } from 'react'
import { AiSparkles16 } from '@vinted/monochrome-icons'
import { Button, Cell, Divider, Icon, Loader, Spacer, Text } from '@vinted/web-ui'

import { v4 as uuid } from 'uuid'

import { useSystemConfiguration } from '@marketplace-web/shared/system-configuration-data'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { urlWithParams } from '@marketplace-web/browser/url-util'
import useHelpCenterSessionTracking from '@marketplace-web/help-center/help-center-session-tracking-util'

import { HelpCenterNavigation } from '@marketplace-web/help-center/help-center-faq-navigation-feature'

import { getFaqEntries } from '@marketplace-web/help-center/help-center-faq-entry-data'
import {
  getAiSummary,
  aiPoweredHelpScreenViewEvent,
  aiPoweredHelpQuerySubmittedEvent,
  aiPoweredHelpSuggestedArticleClickedEvent,
  aiPoweredHelpFeedbackProvidedEvent,
} from '@marketplace-web/help-center/help-center-ai-powered-help-data'
import {
  transformHelpCenterNavigationResponse,
  transformHelpCenterHomeFaqsResponse,
} from '@marketplace-web/help-center/help-center-home-data'

import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import LoadingComponent from './LoadingIndicator'
import HelpCenterSearchInputNew from '../HelpCenterSearchInputNew'
import { HELP_FAQ_ENTRIES_URL } from '../constants/routes'

type Props = {
  searchText: string
}

const HelpCenterAiSearch = ({ searchText }: Props) => {
  const translate = useTranslate('help_center.ai_search')
  const { track } = useTracking()
  const { helpCenterSessionId } = useHelpCenterSessionTracking()
  const [aiPoweredHelpSessionId, setAiPoweredHelpSessionId] = useState<string | null>(null)
  const [isFeedbackGiven, setIsFeedbackGiven] = useState(false)
  const { userCountry } = useSystemConfiguration() || {}
  const breakpoints = useBreakpoint()

  const aiHelpQuerySuggestionsAbTest = useAbTest('ai_help_query_suggestions_web_v1')

  useEffect(() => {
    if (!helpCenterSessionId) return

    track(
      aiPoweredHelpScreenViewEvent({
        helpCenterSessionId,
      }),
    )
  }, [track, helpCenterSessionId])

  const {
    data,
    isLoading: isAnswerLoading,
    error,
    fetch,
  } = useFetch(getAiSummary, undefined, {
    clearDataOnFetch: true,
  })

  useEffect(() => {
    if (!aiPoweredHelpSessionId) {
      setAiPoweredHelpSessionId(uuid())
    }
  }, [aiPoweredHelpSessionId])

  const {
    fetch: fetchFaqs,
    data: faqs,
    isLoading: isFaqsLoading,
  } = useFetch(getFaqEntries, transformHelpCenterHomeFaqsResponse, {
    clearDataOnFetch: false,
  })

  useEffect(() => {
    fetchFaqs()
  }, [fetchFaqs])

  useEffect(() => {
    const hasUserQueryAndSessionId = searchText && aiPoweredHelpSessionId

    if (hasUserQueryAndSessionId) {
      track(
        aiPoweredHelpQuerySubmittedEvent({
          helpCenterSessionId: helpCenterSessionId || '',
          querySessionId: aiPoweredHelpSessionId,
          queryText: searchText,
          country: userCountry,
        }),
      )

      fetch({
        aiPoweredHelpSessionId,
        query: searchText,
        helpCenterSessionId: helpCenterSessionId || '',
        answerFormat: 'html',
      })
    }
  }, [searchText, fetch, helpCenterSessionId, aiPoweredHelpSessionId, track, userCountry])

  const renderIcon = () => (
    <div className="u-flexbox u-align-items-center">
      <Icon name={AiSparkles16} color="primary-default" />
    </div>
  )

  const renderNavigation = () => {
    if (!faqs) return null

    const navigationFaqEntries = transformHelpCenterNavigationResponse(faqs)

    return (
      <HelpCenterNavigation navigationFaqEntries={navigationFaqEntries} isLoading={isFaqsLoading} />
    )
  }

  const renderFaq = (entry, index) => {
    const { id, title } = entry
    const length = data?.faq_entries?.length || 0
    const titleComponent = (
      <div className="faq__ai-search-related-faq-item">
        <Text as="p" text={title} type="body" theme="inherit" bold />
      </div>
    )

    return (
      <Fragment key={index}>
        <Cell
          key={entry.id}
          fullWidthTitle
          styling={breakpoints.portables ? 'default' : 'tight'}
          title={titleComponent}
          type="navigating"
          onClick={() => {
            track(
              aiPoweredHelpSuggestedArticleClickedEvent({
                helpCenterSessionId: helpCenterSessionId || '',
                querySessionId: aiPoweredHelpSessionId || '',
                faqEntryId: entry.id,
              }),
            )
          }}
          url={urlWithParams(`${HELP_FAQ_ENTRIES_URL}/${id}`, {})}
          urlProps={{ target: '_blank', rel: 'noreferrer' }}
          chevron
          testId={`help-center-related-question-${id}`}
        />
        {length - 1 !== index && <Divider />}
      </Fragment>
    )
  }

  const renderFeedback = () => {
    return (
      <>
        <Divider />
        <Spacer size="x-large" />
        <Text as="p" text={translate('feedback_question')} type="title" theme="muted" />
        <Spacer size="large" />
        <>
          {isFeedbackGiven ? (
            <Text as="p" text={translate('feedback_confirmation')} type="subtitle" />
          ) : (
            <>
              <Button
                inline
                size="medium"
                onClick={() => {
                  track(
                    aiPoweredHelpFeedbackProvidedEvent({
                      helpCenterSessionId: helpCenterSessionId || '',
                      querySessionId: aiPoweredHelpSessionId || '',
                      isResponseHelpful: true,
                    }),
                  )
                  setIsFeedbackGiven(true)
                }}
                testId="feedback-button-yes"
                text={translate('feedback_rating_yes')}
              />
              <Spacer orientation="vertical" size="regular" />
              <Button
                inline
                size="medium"
                onClick={() => {
                  track(
                    aiPoweredHelpFeedbackProvidedEvent({
                      helpCenterSessionId: helpCenterSessionId || '',
                      querySessionId: aiPoweredHelpSessionId || '',
                      isResponseHelpful: false,
                    }),
                  )
                  setIsFeedbackGiven(true)
                }}
                testId="feedback-button-no"
                text={translate('feedback_rating_no')}
              />
            </>
          )}
          <Spacer size="x-large" />
          <Divider />
        </>
      </>
    )
  }

  const renderAiAnswer = () => {
    if (error) {
      return (
        <div className="u-flexbox u-align-items-center">
          <Loader state="fail" />
          <Text as="h3" text={translate('error_text')} type="subtitle" />
        </div>
      )
    }

    if (isAnswerLoading) {
      return <LoadingComponent />
    }

    if (!data) {
      return null
    }

    return (
      <>
        <Cell prefix={renderIcon()} styling="tight">
          <Text as="h2" text={translate('result_disclaimer')} type="title" theme="muted" />
        </Cell>
        <Spacer size="large" />
        {data?.answer ? (
          <Text as="p" text={data.answer} type="body" html />
        ) : (
          <Text as="p" text={translate('no_results')} type="body" />
        )}
        {data?.answer ? (
          <>
            <Spacer size="x-large" />
            {renderFeedback()}
          </>
        ) : null}
      </>
    )
  }

  const renderRelatedFaqs = () => {
    if (error) {
      return null
    }

    if (!data) {
      return null
    }

    const hasRelatedFaqs = !!data?.faq_entries

    if (!hasRelatedFaqs) {
      return null
    }

    return (
      <>
        {breakpoints.portables ? <Spacer size="large" orientation="vertical" /> : null}
        {data?.answer ? (
          <Text as="h2" text={translate('related_faqs_title')} type="section-heading" />
        ) : (
          <Text as="h2" text={translate('no_results_faqs_title')} type="section-heading" />
        )}
        <Spacer size="large" />
        {data?.faq_entries.map(renderFaq)}
      </>
    )
  }

  return (
    <>
      <aside className="body-content__sidebar faq__navigation faq__navigation--secondary">
        {renderNavigation()}
      </aside>
      <div
        className={
          aiHelpQuerySuggestionsAbTest?.variant === 'on_2'
            ? 'faq__ai-search-container-nosearchbar'
            : 'faq__ai-search-container-withsearchbar'
        }
      >
        <div className="faq__ai-search">
          <HelpCenterSearchInputNew value={searchText} />
          <Spacer size="x-large" />
          {renderAiAnswer()}
          <Spacer size="x2-large" />
        </div>
        <div className="faq__ai-search-related-faqs">{renderRelatedFaqs()}</div>
      </div>
    </>
  )
}

export default HelpCenterAiSearch
